import React, { useMemo, useState } from 'react';
import {
  Box, IconButton, InputAdornment, Paper, Skeleton, Stack, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, DeleteOutline, EditOutlined, Search } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ConfirmationDialog from './ConfirmationDialog';
import EmptyState from './EmptyState';
import QuickAdd, { QuickAddPreset } from './QuickAdd';

interface SettingsListProps<T> {
  rows: T[];
  loading: boolean;
  rowKey: (row: T) => string;
  /** The row's name; also what quick add checks for repeats. */
  name: (row: T) => string;
  subtitle?: (row: T) => React.ReactNode;
  /** The tile before the name (default: the name's initial). Use SettingsTile. */
  leading?: (row: T, index: number) => React.ReactNode;
  /** Chips or facts shown before the row's actions. */
  meta?: (row: T) => React.ReactNode;
  /** Rows shown muted, such as inactive ones. */
  muted?: (row: T) => boolean;
  /** Text a row is found by; adds a search box once the list is long. */
  searchText?: (row: T) => string;
  canWrite: boolean;
  onEdit?: (row: T) => void;
  /** Rows that may not be edited, with the reason shown as a tooltip. */
  editBlocked?: (row: T) => string | null;
  remove?: {
    run: (row: T) => Promise<unknown>;
    /** e.g. 'group'; used in the confirmation. */
    noun: string;
    /** What else happens, shown in the confirmation. */
    consequence?: (row: T) => string;
    verb?: 'Delete' | 'Archive';
    /** Rows that may not be removed, with the reason shown as a tooltip. */
    blocked?: (row: T) => string | null;
  };
  /** Saves a new order. Shows move up/down on each row. */
  reorder?: (orderedKeys: string[]) => Promise<unknown>;
  /** Rows that keep their place (e.g. inactive ones); nothing moves past them. */
  movable?: (row: T) => boolean;
  quickAdd?: {
    noun: { one: string; many: string };
    placeholder: string;
    create: (name: string) => Promise<unknown>;
    ranges?: boolean;
    presets?: QuickAddPreset[];
  };
  /** Reloads the list after a change. */
  reload: () => void;
  empty: { icon: React.ReactElement; title: string; description: string };
  /** Buttons beside the search box. */
  toolbar?: React.ReactNode;
}

/** The 32px tile at the start of a settings row; pass `color` for colour-coded items. */
export const SettingsTile: React.FC<{ color?: string; children?: React.ReactNode }> = ({ color, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.8rem',
        fontWeight: 700,
        bgcolor: color ? alpha(color, 0.14) : alpha(theme.palette.text.primary, 0.06),
        color: color ?? 'text.secondary',
        '& svg': { fontSize: 18 },
      }}
    >
      {children}
    </Box>
  );
};

/**
 * The one way a settings list looks and behaves: quick add for one or many,
 * compact rows, actions on hover, a confirmed delete, optional reordering.
 */
function SettingsList<T>({
  rows, loading, rowKey, name, subtitle, leading, meta, muted, searchText, canWrite, onEdit, editBlocked, remove, reorder, movable,
  quickAdd, reload, empty, toolbar,
}: SettingsListProps<T>) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [query, setQuery] = useState('');
  const [removing, setRemoving] = useState<T | null>(null);
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);

  const q = query.trim().toLowerCase();
  const shown = useMemo(
    () => (q && searchText ? rows.filter(r => searchText(r).toLowerCase().includes(q)) : rows),
    [rows, q, searchText],
  );
  const canSearch = !!searchText && rows.length > 6;
  // Reordering a filtered list would renumber only part of it.
  const canMove = !!reorder && canWrite && !q;

  const fail = (e: any, fallback: string) =>
    enqueueSnackbar(e?.response?.data?.message || e?.message || fallback, { variant: 'error' });

  const confirmRemove = async () => {
    if (!removing || !remove) return;
    setBusy(true);
    try {
      await remove.run(removing);
      enqueueSnackbar(`${name(removing)} ${remove.verb === 'Archive' ? 'archived' : 'deleted'}`, { variant: 'success' });
      setRemoving(null);
      reload();
    } catch (e) {
      fail(e, `Could not ${(remove.verb ?? 'Delete').toLowerCase()} this ${remove.noun}`);
    } finally {
      setBusy(false);
    }
  };

  const move = async (index: number, by: -1 | 1) => {
    if (!reorder) return;
    const keys = rows.map(rowKey);
    [keys[index], keys[index + by]] = [keys[index + by], keys[index]];
    setMoving(true);
    try {
      await reorder(keys);
      reload();
    } catch (e) {
      fail(e, 'Could not save the new order');
    } finally {
      setMoving(false);
    }
  };

  const actionSx = { color: 'text.secondary', '&:hover': { color: 'text.primary' }, '& svg': { fontSize: 18 } };

  return (
    <Stack spacing={2}>
      {canWrite && quickAdd && (
        <QuickAdd {...quickAdd} existing={rows.map(name)} onAdded={reload} />
      )}

      {(canSearch || toolbar) && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          {canSearch && (
            <TextField
              size="small"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment>
                ),
              }}
              sx={{ width: 260 }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {toolbar}
        </Stack>
      )}

      {loading ? (
        <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          {[0, 1, 2, 3].map(i => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.5, borderTop: i ? 1 : 0, borderColor: 'divider' }}>
              <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: '8px' }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="30%" />
                <Skeleton width="50%" height={16} />
              </Box>
            </Stack>
          ))}
        </Paper>
      ) : rows.length === 0 ? (
        <EmptyState icon={empty.icon} title={empty.title} description={empty.description} dense />
      ) : shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          Nothing matches "{query.trim()}".
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          {shown.map((row, index) => {
            const blockedReason = remove?.blocked?.(row) ?? null;
            const canStep = (by: -1 | 1) => {
              const other = rows[index + by];
              return !!other && (!movable || (movable(row) && movable(other)));
            };
            const hasActions = canWrite && (onEdit || remove || canMove);
            return (
              <Stack
                key={rowKey(row)}
                direction="row"
                alignItems="center"
                spacing={1.5}
                onDoubleClick={canWrite && onEdit && !editBlocked?.(row) ? () => onEdit(row) : undefined}
                sx={{
                  px: 2,
                  minHeight: 56,
                  py: 1,
                  borderTop: index ? 1 : 0,
                  borderColor: 'divider',
                  transition: theme.transitions.create('background-color', { duration: 120 }),
                  '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.025) },
                  '& .row-actions': { opacity: { xs: 1, md: 0 }, transition: theme.transitions.create('opacity', { duration: 120 }) },
                  '&:hover .row-actions, &:focus-within .row-actions': { opacity: 1 },
                }}
              >
                <Box sx={{ flexShrink: 0, display: 'flex' }}>
                  {leading ? leading(row, index) : <SettingsTile>{name(row).charAt(0).toUpperCase()}</SettingsTile>}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, opacity: muted?.(row) ? 0.55 : 1 }}>
                  <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{name(row)}</Typography>
                  {subtitle && (
                    <Typography noWrap component="div" sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.125 }}>
                      {subtitle(row)}
                    </Typography>
                  )}
                </Box>
                {meta && <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>{meta(row)}</Stack>}
                {hasActions && (
                  <Stack direction="row" spacing={0.25} className="row-actions" sx={{ flexShrink: 0 }}>
                    {canMove && (
                      <>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton size="small" sx={actionSx} disabled={moving || !canStep(-1)} onClick={() => move(index, -1)} aria-label="Move up">
                              <ArrowUpward />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton size="small" sx={actionSx} disabled={moving || !canStep(1)} onClick={() => move(index, 1)} aria-label="Move down">
                              <ArrowDownward />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    {onEdit && (
                      <Tooltip title={editBlocked?.(row) ?? 'Edit'}>
                        <span>
                          <IconButton
                            size="small"
                            sx={actionSx}
                            disabled={!!editBlocked?.(row)}
                            onClick={() => onEdit(row)}
                            aria-label={`Edit ${name(row)}`}
                          >
                            <EditOutlined />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {remove && (
                      <Tooltip title={blockedReason ?? remove.verb ?? 'Delete'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!!blockedReason}
                            onClick={() => setRemoving(row)}
                            aria-label={`${remove.verb ?? 'Delete'} ${name(row)}`}
                            sx={{ ...actionSx, '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                )}
              </Stack>
            );
          })}
        </Paper>
      )}

      {remove && (
        <ConfirmationDialog
          open={!!removing}
          onClose={() => !busy && setRemoving(null)}
          onConfirm={confirmRemove}
          loading={busy}
          title={`${remove.verb ?? 'Delete'} ${removing ? name(removing) : ''}?`}
          message={(removing && remove.consequence?.(removing))
            || (remove.verb === 'Archive'
              ? `This ${remove.noun} will no longer be offered. Existing records keep it.`
              : `This ${remove.noun} will be removed. This can't be undone.`)}
          confirmText={remove.verb ?? 'Delete'}
        />
      )}
    </Stack>
  );
}

export default SettingsList;
